import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { PLANS, priceLabel, priceLabelHi, inr, annualBilled, annualSaving, IPD_USAGE_RATE, GST_NOTE } from "../config/pricing";
import { setChosenPlan } from "../lib/plan";

const T = {
  en: {
    perMonth: "/month",
    billed: (v) => `billed yearly as ${v}`,
    ipd: `+ ${inr(IPD_USAGE_RATE)} per IPD patient-day or Day Care session`,
    credits: (c) => `${c} credits included every month`,
    setup: (v) => `One-time setup ${v}`,
    setupWaived: "Setup fee waived",
    save: (total, setup) => `Save ${total}: 2 months free + ${setup} setup waived`,
    cta: "Book a free demo",
    recommended: "Most popular",
    monthly: "Monthly",
    annual: "Annual · 2 months free",
    full: "See full pricing",
  },
  hi: {
    perMonth: "/माह",
    billed: (v) => `सालाना ${v} के रूप में`,
    ipd: `+ प्रति IPD मरीज़-दिन या डे केयर सेशन ${inr(IPD_USAGE_RATE)}`,
    credits: (c) => `हर महीने ${c} क्रेडिट शामिल`,
    setup: (v) => `एक बार का सेटअप ${v}`,
    setupWaived: "सेटअप शुल्क माफ़",
    save: (total, setup) => `${total} की बचत: 2 महीने मुफ़्त + ${setup} सेटअप माफ़`,
    cta: "फ्री डेमो बुक करें",
    recommended: "सबसे लोकप्रिय",
    monthly: "मासिक",
    annual: "सालाना · 2 महीने मुफ़्त",
    full: "पूरी प्राइसिंग देखें",
  },
};

export const CycleSwitch = ({ cycle, setCycle, lang = "en" }) => {
  const t = T[lang];
  return (
    <div className="inline-flex rounded-full border border-navy/10 bg-white p-1" data-testid="pricing-cycle-switch" role="group" aria-label="Billing cycle">
      {[["monthly", t.monthly], ["annual", t.annual]].map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => setCycle(k)}
          aria-pressed={cycle === k}
          className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${cycle === k ? "bg-navy text-white" : "text-navy/70 hover:text-navy"}`}
          data-testid={`pricing-cycle-${k}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export const PlanCards = ({ cycle = "monthly", lang = "en", compact = false }) => {
  const t = T[lang];
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="plan-cards">
      {PLANS.map((plan) => (
        <div
          key={plan.id}
          className={`relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white p-6 pt-7 transition-shadow duration-300 hover:shadow-[0_16px_40px_rgba(11,30,77,0.10)] ${
            plan.recommended ? "border-electric-ink ring-1 ring-electric-ink" : "border-navy/10"
          }`}
          data-testid={`plan-card-${plan.id}`}
        >
          <span aria-hidden className={`absolute inset-x-0 top-0 h-1 ${plan.recommended ? "bg-electric-ink" : "bg-navy/10"}`} />
          {plan.recommended && (
            <span className="absolute right-5 top-4 rounded-full bg-amberx-soft px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amberx" data-testid="plan-recommended-badge">
              {t.recommended}
            </span>
          )}
          <h3 className="font-display text-lg font-bold text-navy">{plan.name}</h3>
          <p className="mt-1.5 text-sm leading-snug text-slate-600">{plan.forWhom[lang]}</p>

          <div className="mt-5 flex items-baseline gap-1.5">
            <span className="font-display text-3xl font-black tracking-tight text-navy" data-testid={`plan-price-${plan.id}`}>
              {lang === "hi" ? priceLabelHi(plan, cycle) : priceLabel(plan, cycle)}
            </span>
            <span className="text-sm font-semibold text-slate-500">{t.perMonth}</span>
          </div>
          <p className="mt-1 text-xs font-semibold text-slate-500">
            {cycle === "annual" ? `${t.billed(inr(annualBilled(plan)))} · ${GST_NOTE[lang]}` : GST_NOTE[lang]}
          </p>
          {plan.ipdUsage && (
            <p className="mt-2 text-xs font-semibold text-navy/70" data-testid={`plan-ipd-${plan.id}`}>{t.ipd}</p>
          )}
          <p className="mt-2 text-xs font-semibold text-teal-ink">{t.credits(plan.credits)}</p>

          {!compact && (
            <ul className="mt-5 flex-1 space-y-2.5">
              {plan.features[lang].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm leading-snug text-navy/80">
                  <CheckCircle size={16} weight="fill" className={`mt-0.5 shrink-0 ${plan.recommended ? "text-electric-ink" : "text-teal-ink"}`} /> {f}
                </li>
              ))}
            </ul>
          )}
          {compact && <div className="flex-1" />}

          <p className="mt-5 text-xs font-bold text-slate-500" data-testid={`plan-setup-${plan.id}`}>
            {cycle === "annual" ? t.setupWaived : t.setup(inr(plan.setup))}
          </p>
          {cycle === "annual" && (
            <p className="mt-1.5 rounded-lg bg-teal/10 px-2.5 py-1.5 text-[11px] font-bold leading-snug text-teal-ink" data-testid={`plan-saving-${plan.id}`}>
              {t.save(inr(annualSaving(plan)), inr(plan.setup))}
            </p>
          )}

          <Link
            to="/contact"
            onClick={() => setChosenPlan(plan.name, inr(plan.monthly))}
            className={`mt-4 rounded-full px-5 py-3 text-center text-sm font-bold transition-colors duration-200 ${
              plan.recommended
                ? "bg-electric-ink text-white hover:bg-[#0F4FA3]"
                : "border border-navy/15 text-navy hover:border-electric-ink hover:text-electric-ink"
            }`}
            data-testid={`plan-cta-${plan.id}`}
          >
            {t.cta}
          </Link>
        </div>
      ))}
    </div>
  );
};
